import { Search, X } from "lucide-react";
import * as React from "react";

import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * Filter-bar building blocks. Composable; the consumer owns state and URL
 * wiring (typically via TanStack Router search params).
 *
 *   <FilterBar>
 *     <FilterSearch
 *       placeholder="Search customers"
 *       value={q}
 *       onChange={setQ}
 *     />
 *     <FilterChip label="Status" count={status.length}>
 *       <CheckboxList ... />
 *     </FilterChip>
 *     <FilterChip label="Owner" valueLabel={ownerName}>
 *       <Combobox ... />
 *     </FilterChip>
 *     <div className="flex-1" />
 *     {hasActive ? <FilterClearAll onClick={clearAll} /> : null}
 *   </FilterBar>
 *
 * Active filters can either live inside their chips (with `count`/`valueLabel`)
 * OR be surfaced as standalone pills via <ActiveFilterPill> in a row below.
 */

export function FilterBar({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      {children}
    </div>
  );
}

interface FilterSearchProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  value: string;
  onChange: (v: string) => void;
}

export function FilterSearch({
  value,
  onChange,
  placeholder = "Search",
  className,
  ...props
}: FilterSearchProps) {
  return (
    <div className={cn("relative w-64", className)}>
      <Search
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      <Input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="pl-9"
        {...props}
      />
    </div>
  );
}

interface FilterChipProps {
  label: string;
  /** Number of selected values, shown as a count badge when > 0. */
  count?: number;
  /** Single-value summary, e.g. "Acme Corp" — shown after a separator. */
  valueLabel?: string;
  children: React.ReactNode;
  className?: string;
}

export function FilterChip({
  label,
  count,
  valueLabel,
  children,
  className,
}: FilterChipProps) {
  const isActive = (count ?? 0) > 0 || Boolean(valueLabel);
  return (
    <Popover>
      <PopoverTrigger
        className={cn(
          "inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          isActive
            ? "border-brand bg-brand-light text-brand hover:bg-brand-light"
            : "border-input bg-background text-foreground hover:bg-muted",
          className,
        )}
      >
        <span>{label}</span>
        {valueLabel ? (
          <>
            <span className="text-muted-foreground">·</span>
            <span className="max-w-[12rem] truncate">{valueLabel}</span>
          </>
        ) : null}
        {count && count > 0 ? (
          <span className="inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-brand px-1 text-[10px] font-semibold text-white">
            {count}
          </span>
        ) : null}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-3">
        {children}
      </PopoverContent>
    </Popover>
  );
}

export function ActiveFilterPill({
  label,
  onRemove,
  className,
}: {
  label: string;
  onRemove: () => void;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex h-6 items-center gap-1 rounded-full bg-brand-light pl-2 pr-1 text-xs font-medium text-brand",
        className,
      )}
    >
      {label}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label}`}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full hover:bg-brand/20"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

export function FilterClearAll({
  onClick,
  className,
}: {
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "text-xs font-medium text-brand hover:underline focus-visible:outline-none focus-visible:underline",
        className,
      )}
    >
      Clear all
    </button>
  );
}
